/**
 * "Criar festa" and the magic-link landing. build-prompt section 6.
 *
 * Child's name, age, date, time, venue, deadline, host name, host email.
 * Nothing else, per section 6: every extra field is one more reason to give up
 * on a phone.
 */

import { useEffect, useState } from 'react';
import { ApiError, host } from '../api.js';
import { navigate } from '../router.js';
import { holdFile } from './pendingFile.js';
import { downloadTemplate } from './template.js';

const TIMEZONE = 'America/Sao_Paulo';

export function CreateEvent() {
  const [values, setValues] = useState({
    childName: '',
    ageTurning: '',
    date: '',
    // Empty on purpose: a pre-filled time is a guess about somebody's party,
    // and a guess she does not notice is worse than a field she has to fill.
    time: '',
    venue: '',
    venueMapsUrl: '',
    rsvpDeadline: '',
    hostName: '',
  });
  const [me, setMe] = useState<'checking' | 'ready'>('checking');
  const [showMap, setShowMap] = useState(false);
  const [listFile, setListFile] = useState<File | null>(null);
  const [issues, setIssues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * Creating a party is an authenticated action now: the address comes from
   * the mailbox she proved, never from a field. Anyone without a session is
   * sent to the front door to ask for a link.
   */
  useEffect(() => {
    host
      .me()
      .then((who) => {
        if (!who.signedIn) return navigate('/', true);
        if (who.eventId) return navigate(`/painel/${who.eventId}`, true);
        setMe('ready');
      })
      .catch(() => navigate('/', true));
  }, []);

  const set = (key: keyof typeof values) => (value: string) =>
    setValues((current) => ({ ...current, [key]: value }));

  /**
   * Picking the day fills the deadline with five days before it.
   *
   * It was a ninth field asking her to do arithmetic about her own party. A
   * sensible default she can override is the same information with none of the
   * work.
   */
  const setDate = (value: string) =>
    setValues((current) => ({
      ...current,
      date: value,
      rsvpDeadline: current.rsvpDeadline || fiveDaysBefore(value),
    }));

  async function submit() {
    // Now that the time starts empty, a missing one would quietly become
    // midnight. Ask for it instead.
    const missing: Record<string, string> = {};
    if (!values.date) missing.startsAt = 'Escolha o dia da festa.';
    if (!values.time) missing.time = 'Escolha a hora da festa.';
    if (Object.keys(missing).length > 0) {
      setIssues(missing);
      return;
    }

    setBusy(true);
    setError(null);
    setIssues({});

    try {
      // The form collects date and time separately because two small native
      // pickers beat one datetime-local on a phone. They are joined here with
      // the event's offset so the server stores a real instant.
      const startsAt = toInstant(values.date, values.time);

      const { eventId } = await host.createEvent({
        childName: values.childName,
        ageTurning: Number(values.ageTurning),
        startsAt,
        timezone: TIMEZONE,
        venue: values.venue,
        venueMapsUrl: values.venueMapsUrl || undefined,
        rsvpDeadline: values.rsvpDeadline || values.date,
        hostName: values.hostName,
      });

      // Creating the party signs her in for that party, so the list can go up
      // now rather than after a trip through her inbox.
      holdFile(listFile);
      navigate(listFile ? `/importar/${eventId}` : `/painel/${eventId}`);
    } catch (err) {
      if (err instanceof ApiError && err.issues.length > 0) {
        setIssues(
          Object.fromEntries(err.issues.map((i) => [i.field, i.message])),
        );
      } else {
        setError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  }

  if (me === 'checking') return <div className="spinner">Um instante…</div>;

  return (
    <div className="page">
      <h1>Criar festa</h1>
      <p className="muted">
        Sem cadastro e sem senha. Tudo é apagado 2 dias depois da festa.
      </p>

      {error && <div className="banner error">{error}</div>}

      <div className="group">
        <div className="group-eyebrow">A festa</div>

        <div className="row-2 name-age">
          <Field label="Aniversariante" error={issues.childName}>
            <input
              value={values.childName}
              onChange={(e) => set('childName')(e.target.value)}
              placeholder="Ana"
              autoFocus
            />
          </Field>
          <Field label="Idade" error={issues.ageTurning}>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={18}
              value={values.ageTurning}
              onChange={(e) => set('ageTurning')(e.target.value)}
            />
          </Field>
        </div>

        <div className="row-2 when">
          <Field label="Dia" error={issues.startsAt}>
            <input
              type="date"
              value={values.date}
              onChange={(e) => setDate(e.target.value)}
            />
          </Field>
          <Field label="Hora" error={issues.time}>
            <input
              type="time"
              value={values.time}
              onChange={(e) => set('time')(e.target.value)}
            />
          </Field>
        </div>

        <Field label="Onde" error={issues.venue}>
          <input
            value={values.venue}
            onChange={(e) => set('venue')(e.target.value)}
            placeholder="Buffet Alegria, Campinas"
          />
        </Field>

        {showMap ? (
          <Field label="Link do mapa">
            <input
              value={values.venueMapsUrl}
              onChange={(e) => set('venueMapsUrl')(e.target.value)}
              placeholder="https://maps.app.goo.gl/…"
              inputMode="url"
              autoFocus
            />
          </Field>
        ) : (
          <button
            type="button"
            className="linkish"
            onClick={() => setShowMap(true)}
          >
            + Link do mapa
          </button>
        )}

        {/* Only once there is a day to count back from. An empty date picker
            labelled "Confirmar até ," is a question she cannot answer yet. */}
        {values.date && (
        <div className="hint-inline">
          <span>
            Confirmar até <strong>{formatDay(values.rsvpDeadline)}</strong>
          </span>
          <input
            type="date"
            value={values.rsvpDeadline}
            onChange={(e) => set('rsvpDeadline')(e.target.value)}
            aria-label="Confirmar até"
            style={{ width: '9.5rem', minHeight: '40px' }}
          />
        </div>
        )}
      </div>

      <div className="group">
        <div className="group-eyebrow">Você</div>

        <Field label="Seu nome" error={issues.hostName}>
          <input
            value={values.hostName}
            onChange={(e) => set('hostName')(e.target.value)}
            placeholder="Sandra"
          />
        </Field>

        </div>

      <div className="group">
        <div className="group-eyebrow">A lista</div>
        <p className="muted" style={{ margin: '0 0 0.5rem', fontSize: '0.9rem' }}>
          Se já tem a lista da turma, mande agora. Dá para fazer depois também.
        </p>

        <label htmlFor="list-file">Planilha (opcional)</label>
        <input
          id="list-file"
          type="file"
          accept=".xlsx,.csv,.tsv,text/csv,text/tab-separated-values"
          onChange={(e) => setListFile(e.target.files?.[0] ?? null)}
        />
        <button type="button" className="linkish" onClick={downloadTemplate}>
          Baixar modelo de planilha
        </button>
      </div>

      <div className="sticky-action">
        <button className="primary" onClick={submit} disabled={busy}>
          {busy ? 'Criando…' : listFile ? 'Criar festa e ver a lista' : 'Criar festa'}
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label>{label}</label>
      {children}
      {error && <div className="field-error">{error}</div>}
    </div>
  );
}

/**
 * Join a date and a time into an instant in the event's zone.
 *
 * Brazil has not observed daylight saving since 2019, so a fixed -03:00 is
 * correct today and this is written to be easy to find if that ever changes.
 */
/** Five days before the party, the default she can change. */
function fiveDaysBefore(date: string): string {
  if (!date) return '';
  const day = new Date(`${date}T12:00:00Z`);
  day.setUTCDate(day.getUTCDate() - 5);
  return day.toISOString().slice(0, 10);
}

/** "6 de setembro", for the inline deadline line. */
function formatDay(date: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${date}T12:00:00Z`));
}

function toInstant(date: string, time: string): string {
  if (!date || !time) return '';
  return `${date}T${time}:00-03:00`;
}

/** The magic-link landing: spend the token, then go to the dashboard. */
export function Enter({ token }: { token: string }) {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    host
      .consumeLink(token)
      .then(({ eventId }) => {
        // Her party if she has one, the create form if she does not.
        navigate(eventId ? `/painel/${eventId}` : '/criar', true);
      })
      .catch((err: Error) => setError(err.message));
  }, [token]);

  if (error) {
    return (
      <div className="page">
        <div className="banner error">{error}</div>
        <h1>Este link não vale mais</h1>
        <p className="muted">
          Links de acesso valem 15 minutos e só funcionam uma vez. Peça outro
          abaixo.
        </p>
        <RequestLink />
      </div>
    );
  }

  return <div className="spinner">Entrando…</div>;
}

export function RequestLink() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  if (sent) {
    return (
      <div className="banner ok">
        Se este e-mail tiver uma festa, o link já está a caminho.
      </div>
    );
  }

  return (
    <div className="card">
      <label htmlFor="link-email">Seu e-mail</label>
      <input
        id="link-email"
        type="email"
        inputMode="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <button
        className="primary"
        style={{ marginTop: '0.75rem' }}
        disabled={busy || !email.includes('@')}
        onClick={async () => {
          setBusy(true);
          try {
            await host.requestLink(email);
            setSent(true);
          } finally {
            setBusy(false);
          }
        }}
      >
        Mandar link
      </button>
    </div>
  );
}
